import React, { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { X, Search, Loader2 } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import api from "@/lib/api";

/**
 * Chat user picker — search-as-you-type + multi-select for group invites.
 *
 * Data source: GET /api/chat/users/search — already gates on 18+ and
 * bidirectional blocks, and returns only public fields (user_id, name,
 * picture, role). No email or private data ever reaches the picker.
 *
 * Props:
 *  - value: [{ user_id, name, picture, role }] selected users
 *  - onChange: (next) => void
 *  - testidPrefix: string (default "chat-picker")
 *  - max: hard cap on selection (default 50)
 */
export default function ChatUserPicker({ value = [], onChange, testidPrefix = "chat-picker", max = 50 }) {
  const { t } = useI18n();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);
  const boxRef = useRef(null);

  useEffect(() => {
    // Debounced search — 200ms after last keystroke.
    if (timerRef.current) clearTimeout(timerRef.current);
    if (q.trim().length < 2) {
      setResults([]);
      return;
    }
    timerRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/chat/users/search", { params: { q: q.trim(), limit: 15 } });
        setResults(data.items || []);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 200);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [q]);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const selectedIds = new Set(value.map((u) => u.user_id));
  const suggestions = results.filter((u) => !selectedIds.has(u.user_id));

  const add = (u) => {
    if (selectedIds.has(u.user_id)) return;
    if (value.length >= max) return;
    onChange([...value, u]);
    setQ("");
    setResults([]);
    setOpen(false);
  };
  const remove = (uid) => onChange(value.filter((u) => u.user_id !== uid));

  return (
    <div ref={boxRef} className="relative space-y-2" data-testid={`${testidPrefix}-root`}>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid={`${testidPrefix}-selected`}>
          {value.map((u) => (
            <Badge
              key={u.user_id}
              variant="secondary"
              className="pl-1 pr-1 gap-1 rounded-full"
              data-testid={`${testidPrefix}-chip-${u.user_id}`}
            >
              {u.picture ? (
                <img src={u.picture} alt="" className="w-4 h-4 rounded-full object-cover" />
              ) : (
                <span className="w-4 h-4 rounded-full bg-slate-300 inline-block" />
              )}
              <span className="text-xs">{u.name}</span>
              <button
                type="button"
                onClick={() => remove(u.user_id)}
                className="ml-0.5 hover:text-red-600"
                title={t("chat_group_picker_remove")}
                data-testid={`${testidPrefix}-remove-${u.user_id}`}
              >
                <X size={11} />
              </button>
            </Badge>
          ))}
        </div>
      )}
      <div className="relative">
        <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          data-testid={`${testidPrefix}-search`}
          value={q}
          onFocus={() => setOpen(true)}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          placeholder={t("chat_group_picker_placeholder")}
          className="pl-7"
        />
        {loading && (
          <Loader2 size={13} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 animate-spin" />
        )}
      </div>

      {open && q.trim().length >= 2 && (
        <div
          className="absolute z-30 left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-64 overflow-y-auto"
          data-testid={`${testidPrefix}-results`}
        >
          {suggestions.length === 0 && !loading && (
            <div className="p-3 text-xs text-slate-500 italic" data-testid={`${testidPrefix}-empty`}>
              {t("chat_group_picker_no_results")}
            </div>
          )}
          {suggestions.map((u) => (
            <button
              type="button"
              key={u.user_id}
              onClick={() => add(u)}
              className="w-full flex items-center gap-2 px-3 py-2 hover:bg-emerald-50 text-left"
              data-testid={`${testidPrefix}-result-${u.user_id}`}
            >
              {u.picture ? (
                <img src={u.picture} alt="" className="w-7 h-7 rounded-full object-cover" />
              ) : (
                <div className="w-7 h-7 rounded-full bg-slate-200" />
              )}
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-slate-900 truncate">{u.name}</div>
                {u.role && <div className="text-[10px] text-slate-500 uppercase tracking-wider">{u.role}</div>}
              </div>
            </button>
          ))}
        </div>
      )}
      {open && q.trim().length > 0 && q.trim().length < 2 && (
        <div
          className="absolute z-30 left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg p-3 text-xs text-slate-500 italic"
          data-testid={`${testidPrefix}-min-hint`}
        >
          {t("chat_group_picker_min_chars")}
        </div>
      )}
    </div>
  );
}
